import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';

const { FiX, FiLock, FiMail, FiUser, FiChevronRight } = FiIcons;

const LoginModal = () => {
  const { loginModalOpen, closeLoginModal, loginWithPassword } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!loginModalOpen) return null;

  const resetAndClose = () => {
    setForm({ email: '', password: '' });
    setError('');
    closeLoginModal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const email = form.email.trim().toLowerCase();
    const password = form.password;
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await loginWithPassword(email, password);
      if (result.error) {
        setError(result.error);
        return;
      }
      resetAndClose();
      const u = result.user || {};
      if (u.is_superuser) {
        navigate('/admin');
      } else if (u.blog_subdomain) {
        navigate(`/${u.blog_subdomain}`);
      } else {
        navigate('/');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={resetAndClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div
          className="h-2 w-full"
          style={{ backgroundColor: 'var(--brand-primary)' }}
        />
        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <img
                src={settings.app_logo_url || '/logo.png'}
                alt={`${settings.app_name} logo`}
                className="w-10 h-10 object-contain rounded-lg"
                onError={(e) => { e.currentTarget.src = '/logo.png'; }}
              />
              <div>
                <h2 className="text-xl font-bold text-gray-900">{settings.app_name}</h2>
                <p className="text-xs text-gray-500">Admin sign in</p>
              </div>
            </div>
            <button onClick={resetAndClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-6">Restricted access for staff only.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="you@example.com"
                  autoFocus
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full flex justify-center items-center gap-2 py-2.5 px-4 rounded-lg shadow-sm text-sm font-medium text-white transition-all disabled:opacity-75 disabled:cursor-not-allowed hover:opacity-90"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              {submitting ? 'Signing in...' : 'Sign In'}
            </button>

            <div className="pt-2 text-center">
              <button
                type="button"
                onClick={() => { closeLoginModal(); navigate('/reset-request'); }}
                className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 transition-colors"
              >
                Reset password <SafeIcon icon={FiChevronRight} className="w-3 h-3" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
